import React, { useEffect, useState } from "react";
import { Calculator, Save, Loader2, Eye, RotateCcw } from "lucide-react";
import { getSimulatorSettings, updateSimulatorSettings } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { estimate } from "../../intendance/Simulator";
import { log } from "../../../lib/log";

const FIELDS = [
  ["clean_m2", "Nettoyage", "€ HT / m² / mois"], ["conso_per_person", "Consommables", "€ HT / personne / mois"], ["green_m2", "Espaces verts", "€ HT / m² / mois"],
  ["handy_hour", "Homme toutes mains", "€ HT / heure"], ["bundle_discount", "Remise Intendance", "% sur le total séparé"], ["admin_hours_per_provider", "Temps de gestion", "h / mois / prestataire"],
];
const inputCls = "w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:bg-neutral-50";
const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const SAMPLE = { surface: 400, effectif: 20, ext: 500, handy: 4 };

/** Ratios du simulateur de la page Intendance (édition master, aperçu en direct sur un site type). */
const SimulatorEditor = ({ isMaster }) => {
  const [f, setF] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  useEffect(() => { getSimulatorSettings().then((r) => { const { defaults: d, ...rates } = r.data; setDefaults(d); setF(rates); }).catch((e) => log.error("simulator settings:", e)); }, []);

  const save = async () => {
    setSaving(true);
    try { const r = await updateSimulatorSettings(f); setF(r.data); toast({ title: "Ratios du simulateur enregistrés ✓" }); }
    catch (e) { toast({ title: "Erreur", description: e?.response?.data?.detail || "Échec", variant: "destructive" }); }
    finally { setSaving(false); }
  };
  const nums = f && Object.fromEntries(Object.entries(f).map(([k, v]) => [k, Number(v) || 0]));
  const preview = nums && estimate(SAMPLE, nums);

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 mb-5" data-testid="simulator-editor">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Calculator size={18} className="text-brand" />
        <p className="font-semibold text-neutral-900 text-sm">Ratios du simulateur Intendance</p>
        <a href="/intendance#simulateur" target="_blank" rel="noreferrer" className="text-xs text-brand hover:underline flex items-center gap-1"><Eye size={12} /> /intendance#simulateur</a>
        {!isMaster && <span className="text-xs text-neutral-400">(modification réservée au super-admin)</span>}
      </div>
      {!f ? <p className="text-sm text-neutral-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</p> : (<>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {FIELDS.map(([k, label, unit]) => (
            <label key={k}><span className="block text-[11px] font-semibold text-neutral-500 mb-1">{label} <span className="font-normal">— {unit}</span></span>
              <input type="number" step="0.05" min={0} className={inputCls} value={f[k]} disabled={!isMaster} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`sim-rate-${k}`} /></label>
          ))}
        </div>
        <p className="text-xs text-neutral-500 mt-3" data-testid="sim-rate-preview">
          Aperçu site type (400 m², 20 pers., 500 m² d'extérieurs, 4 h de petits travaux) : {eur(preview.separate)} séparés → <b>{eur(preview.bundle)}</b> en Intendance, économie <b>{eur(preview.saving)}</b> / mois, {preview.hours} h libérées.
        </p>
        {isMaster && (
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setF({ ...defaults })} className="flex items-center gap-1.5 text-sm text-neutral-600 border border-neutral-300 px-3 py-2 rounded-md" data-testid="sim-rate-reset"><RotateCcw size={14} /> Valeurs par défaut</button>
            <button onClick={save} disabled={saving} className="flex items-center gap-1.5 bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60" data-testid="sim-rate-save">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer</button>
          </div>
        )}
      </>)}
    </div>
  );
};

export default SimulatorEditor;
